import { DataSource } from '@angular/cdk/table'
import { Injectable } from '@angular/core'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'
import { LaskunAsiakas } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { BehaviorSubject, combineLatest, Observable, of as observableOf } from 'rxjs'
import { filter, map, switchMap, tap } from 'rxjs/operators'
import { KayttajaService } from '../../_angular/service/kayttaja.service'

interface Hakutiedot {
  sivutustiedot: PageEvent
  lajittelutiedot: Sort
}

@Injectable()
export class AsiakkaatFirestoreDataSource extends DataSource<LaskunAsiakas> {
  private _hakutiedot = this._annaOletusHakutiedot()
  private _hakutiedotSubject = new BehaviorSubject<Hakutiedot>(this._hakutiedot)

  private _lataaSubject = new BehaviorSubject<boolean>(true)
  lataaObservable: Observable<boolean> = this._lataaSubject.asObservable()

  private _rivienMaaraSubject = new BehaviorSubject<number>(0)
  rivienMaaraObservable: Observable<number> = this._rivienMaaraSubject.asObservable()

  private _kaikkiAsiakkaatObservable: Observable<LaskunAsiakas[]>
  asiakkaatObservable: Observable<LaskunAsiakas[]>

  constructor(
    private _kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _laskuUriService: LaskuUriService,
  ) {
    super()

    this._kayttajaService.kayttajanTiedotObservable.pipe(filter(kayttaja => !kayttaja)).subscribe(() => {
      this._hakutiedot = this._annaOletusHakutiedot()
      this._hakutiedotSubject.next(this._hakutiedot)
    })

    this._kaikkiAsiakkaatObservable = this._kayttajaService.kayttajanTiedotObservable.pipe(
      tap(() => this._lataaSubject.next(true)),
      switchMap(kayttaja => {
        if (!kayttaja) {
          return observableOf<LaskunAsiakas[]>([])
        }
        const uri = this._laskuUriService.getLaskuAsiakkaatCollectionUri(kayttaja.asiakasAvain)
        return this._firebaseLemonaid
          .firestoreCollection<LaskunAsiakas>(uri)
          .listenSnapshots()
          .pipe(
            map(snapshots =>
              snapshots.map(snapshot => {
                const asiakas = snapshot.data()
                asiakas.avain = snapshot.id
                return asiakas
              }),
            ),
          )
      }),
      tap(() => {
        setTimeout(() => {
          this._lataaSubject.next(false)
        }, 0)
      }),
      lemonShare(),
    )

    this.asiakkaatObservable = combineLatest([this._kaikkiAsiakkaatObservable, this._hakutiedotSubject, this.lataaObservable]).pipe(
      map(([asiakkaat, hakutiedot, lataa]) => {
        if (lataa) {
          return []
        }
        const jarjestetyt = this._suodataJaLajittele(asiakkaat, hakutiedot.lajittelutiedot)
        this._rivienMaaraSubject.next(jarjestetyt.length)
        return this._annaSivu(jarjestetyt, hakutiedot.sivutustiedot)
      }),
      lemonShare(),
    )
  }

  private _suodataJaLajittele(asiakkaat: LaskunAsiakas[], lajittelutiedot: Sort): LaskunAsiakas[] {
    const naytettavat = asiakkaat.filter(asiakas => !asiakas.poistettu)
    const { active, direction } = lajittelutiedot
    if (!active || !direction) {
      return naytettavat
    }
    const suunta = direction === 'asc' ? 1 : -1
    return naytettavat.sort((a, b) => this._vertaaKentat(a, b, active) * suunta)
  }

  private _annaSivu(asiakkaat: LaskunAsiakas[], sivutustiedot: PageEvent): LaskunAsiakas[] {
    const alku = sivutustiedot.pageIndex * sivutustiedot.pageSize
    return asiakkaat.slice(alku, alku + sivutustiedot.pageSize)
  }

  private _vertaaKentat(a: LaskunAsiakas, b: LaskunAsiakas, sarake: string): number {
    const arvoA = this._annaLajitteluarvo(a, sarake).toLowerCase()
    const arvoB = this._annaLajitteluarvo(b, sarake).toLowerCase()
    return arvoA.localeCompare(arvoB)
  }

  private _annaLajitteluarvo(asiakas: LaskunAsiakas, sarake: string): string {
    switch (sarake) {
      case 'nimi':
        return asiakas.nimi ?? ''
      case 'ytunnus':
        return asiakas.ytunnus ?? ''
      case 'katuosoite':
        return asiakas.katuosoite ?? ''
      case 'postinro':
        return asiakas.postinro ?? ''
      case 'postitoimipaikka':
        return asiakas.postitmp ?? ''
      case 'maa':
        return asiakas.maa ?? ''
      default:
        return ''
    }
  }

  private _annaOletusHakutiedot(): Hakutiedot {
    return {
      sivutustiedot: { length: 0, pageIndex: 0, pageSize: 20 },
      lajittelutiedot: { active: 'nimi', direction: 'asc' },
    }
  }

  getSort(): Sort {
    return this._hakutiedot.lajittelutiedot
  }

  getPage(): PageEvent {
    return this._hakutiedot.sivutustiedot
  }

  changeSort(sort: Sort) {
    this._hakutiedot = { ...this._hakutiedot, lajittelutiedot: sort, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  changePage(page: PageEvent) {
    this._hakutiedot = { ...this._hakutiedot, sivutustiedot: page }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  connect(): Observable<LaskunAsiakas[]> {
    return this.asiakkaatObservable
  }

  disconnect() {}
}
